import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { useQueryClient } from "@tanstack/react-query";
import { acceptInvitation } from "@/lib/Auth/authApi";
import { useAuthStore } from "@/lib/Auth/authStore";
import { resolveTenantForOrg } from "@/app/Organisations/Utils/tenant";
import { supplierIdToParam } from "@/app/Suppliers/Utils/globalSupplier";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateMeMutation } from "@/app/Profile/GraphQL/profileMutations";
import { Button } from "@/components/catalyst/button";
import { Heading } from "@/components/catalyst/heading";
import { Text } from "@/components/catalyst/text";

type Phase = "accepting" | "name" | "done" | "error";

export function AcceptInvitationScreen() {
  const t = useTranslations("auth.invitation");
  const navigate = useNavigate();
  const { token } = useSearch({
    from: "/_no_org/accept-invitation",
  });
  const [phase, setPhase] = useState<Phase>("accepting");
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const setToken = useAuthStore((state) => state.setToken);
  const queryClient = useQueryClient();
  const hasStarted = useRef(false);
  const { mutateAsync: updateMe, isPending: isSaving } =
    useGqlMutation(updateMeMutation);

  const proceed = useCallback(async () => {
    const orgs = await queryClient.fetchQuery<{
      organisations: { id: string }[];
    }>({
      queryKey: ["auth", "organisations"],
    });
    // URL is the source of truth: navigate into the scoped tree and let
    // appLayoutRoute.beforeLoad adopt the org from the path into the store.
    const firstOrg = orgs?.organisations?.[0];
    setPhase("done");
    if (firstOrg) {
      navigate({
        to: "/$tenant/$orgId/supplier/{-$supplierId}",
        params: {
          tenant: resolveTenantForOrg(firstOrg.id),
          orgId: firstOrg.id,
          supplierId: supplierIdToParam(null),
        },
      });
    } else {
      navigate({ to: "/org" });
    }
  }, [navigate, queryClient]);

  useEffect(() => {
    if (!token || hasStarted.current) return;
    hasStarted.current = true;

    acceptInvitation(token)
      .then(async (response) => {
        setToken(response.token);
        await queryClient.invalidateQueries({ queryKey: ["auth"] });

        if (response.user.name?.trim()) {
          await proceed();
        } else {
          setPhase("name");
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t("failedToAccept"));
        setPhase("error");
      });
  }, [token, setToken, queryClient, proceed, t]);

  async function handleSaveName(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("nameRequired"));
      return;
    }

    try {
      await updateMe({ input: { name: trimmed } });
      await proceed();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failedToSaveName"));
    }
  }

  if (!token) {
    return (
      <div className="max-w-md space-y-4 p-6">
        <Heading>{t("invalidTitle")}</Heading>
        <Text>{t("invalidDescription")}</Text>
        <Button onClick={() => navigate({ to: "/sign-in" })}>
          {t("goToSignIn")}
        </Button>
      </div>
    );
  }

  if (phase === "name") {
    return (
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-xs space-y-4">
          <div className="text-center">
            <Heading>{t("nameTitle")}</Heading>
            <Text className="mt-1">{t("nameDescription")}</Text>
          </div>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSaveName} className="space-y-3">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                {t("nameLabel")}
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder={t("namePlaceholder")}
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? t("saving") : t("continue")}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="max-w-md space-y-4 p-6">
        <Heading>{t("acceptedTitle")}</Heading>
        <Text>{t("redirecting")}</Text>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="max-w-md space-y-4 p-6">
        <Heading>{t("errorTitle")}</Heading>
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
        <Button onClick={() => navigate({ to: "/sign-in" })}>
          {t("goToSignIn")}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-md space-y-4 p-6">
      <Heading>{t("acceptingTitle")}</Heading>
    </div>
  );
}
